import apiClient from '@/lib/api-client'
import type {
  User,
  Wspolnota,
  Lokal,
  StawkaMedium,
  StawkaMediumCreate,
  StawkaMediumUpdate,
  PozycjaCzynszowa,
  Klatka,
  PozycjaCynszowaCreate,
  PozycjaCynszowaUpdate,
  Licznik,
  OdczytLicznika,
  HistoriaLicznikaResponse,
  EmailHistory,
  MonthlyEmailRequest,
  BulkEmailRequest,
  TestEmailRequest,
  EmailTaskResponse,
  Mieszkaniec,
  OplataIndywidualna,
  OplataIndywidualnaCreate,
  OplataIndywidualnaUpdate,
  Administrator,
  AdministratorUpdate,
  RozliczenieOkresowe,
  RozliczenieDetailed,
  ImportBankowy,
  ImportBankowyWithTransakcje,
  TransakcjaBankowa,
  MatchTransactionRequest,
  CreatePaymentsRequest,
  CreatePaymentsResponse,
  ZadluzeniaRaport,
  ZuzycieMediowRaport,
  PorownanieRaport,
  WspolnotaDashboardStats,
  NotificationStats,
  PendingNotification,
  NotificationTriggerRequest,
  NotificationTriggerResponse,
  NotificationHistoryResponse,
  OwnerTokenRequest,
  OwnerTokenResponse,
  OwnerDashboard,
  OwnerPaymentHistory,
  OwnerBalanceInfo,
  OwnerMeterInfo,
  OwnerSettlementHistory,
  OwnerEmailHistoryResponse,
  ResidentActivateResponse,
  ResidentMe,
  ObiektDodatkowy,
  ObiektDodatkowyCreate,
  ObiektDodatkowyUpdate
} from '@/types'
import type {
  ZadluzenieApi,
  ZadluzenieCreateApi,
  ZadluzenieUpdateApi,
  PlatnoscApi,
  PlatnoscCreateApi,
  PlatnoscUpdateApi,
  SaldoLokaluApi,
} from '@/types/zadluzenia-platnosci'

// Auth API
export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  access_token: string
  refresh_token?: string
  token_type: string
  user: User
}

export const authApi = {
  login: (credentials: LoginCredentials) =>
    apiClient.post<LoginResponse>('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get<User>('/auth/me'),
}

// ============================================================================
// Resident API (JWT-based) — activate, /me, change-password
// ============================================================================
export const residentApi = {
  // Aktywacja konta mieszkańca po kliknięciu w link z emaila zaproszenia.
  // Zwraca JWT (access + refresh) — automatycznie zapisywany w store po sukcesie.
  activate: (token: string, new_password: string) =>
    apiClient.post<ResidentActivateResponse>('/resident/activate', {
      token,
      new_password,
    }),
  // Dane mieszkańca z JWT (role-scoped: wymaga roli resident).
  me: () => apiClient.get<ResidentMe>('/resident/me'),
  // Zmiana hasła przez mieszkańca.
  changePassword: (old_password: string, new_password: string) =>
    apiClient.post('/resident/change-password', { old_password, new_password }),
}

// 2026-04-20: dashboardApi usunięte — wywoływał nieistniejący /api/dashboard/stats.
// Patrz TODO_NAPRAWA.md P2-4.

// Wspólnoty API
export const wspolnotyApi = {
  getAll: (params?: any) => apiClient.get<Wspolnota[]>('/wspolnoty', { params }),
  getById: (id: number) => apiClient.get<Wspolnota>(`/wspolnoty/${id}`),
  create: (data: Partial<Wspolnota>) => apiClient.post<Wspolnota>('/wspolnoty', data),
  update: (id: number, data: Partial<Wspolnota>) => apiClient.put<Wspolnota>(`/wspolnoty/${id}`, data),
  delete: (id: number) => apiClient.delete(`/wspolnoty/${id}`),
}

// Lokale API
export const lokaleApi = {
  getAll: (params?: any) => apiClient.get<Lokal[]>('/lokale', { params }),
  getById: (id: number) => apiClient.get<Lokal>(`/lokale/${id}`),
  create: (data: Partial<Lokal>) => apiClient.post<Lokal>('/lokale', data),
  update: (id: number, data: Partial<Lokal>) => apiClient.put<Lokal>(`/lokale/${id}`, data),
  delete: (id: number) => apiClient.delete(`/lokale/${id}`),
}

// Klatki API
export const klatkiApi = {
  getAll: (params?: { wspolnota_id?: number }) =>
    apiClient.get<Klatka[]>('/klatki', { params }),
  getById: (id: number) =>
    apiClient.get<Klatka>(`/klatki/${id}`),
  create: (data: Omit<Klatka, 'id' | 'created_at' | 'updated_at' | 'liczba_lokali'>) =>
    apiClient.post<Klatka>('/klatki', data),
  update: (id: number, data: Partial<Omit<Klatka, 'id' | 'created_at' | 'updated_at' | 'liczba_lokali'>>) =>
    apiClient.put<Klatka>(`/klatki/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/klatki/${id}`),
  getLokale: (id: number) =>
    apiClient.get<Lokal[]>(`/klatki/${id}/lokale`),
  autoDetect: (wspolnota_id: number) =>
    apiClient.post<{ wspolnota_id: number; detected_klatki: number; created_klatki: number; updated_lokale: number; details: Record<string, number> }>('/klatki/auto-detect', { wspolnota_id })
}

// Stawki Mediów API
export const stawkiMediowApi = {
  getAll: (params?: { wspolnota_id?: number; typ_medium?: string; aktualne?: boolean }) =>
    apiClient.get<StawkaMedium[]>('/stawki-mediow', { params }),
  getById: (id: number) =>
    apiClient.get<StawkaMedium>(`/stawki-mediow/${id}`),
  create: (data: StawkaMediumCreate) =>
    apiClient.post<StawkaMedium>('/stawki-mediow', data),
  update: (id: number, data: StawkaMediumUpdate) =>
    apiClient.put<StawkaMedium>(`/stawki-mediow/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/stawki-mediow/${id}`),
}

// Pozycje Czynszowe API
export const pozycjeCynszoweApi = {
  getAll: (params?: { wspolnota_id?: number; aktywna?: boolean }) =>
    apiClient.get<PozycjaCzynszowa[]>('/pozycje-czynszowe', { params }),
  getById: (id: number) =>
    apiClient.get<PozycjaCzynszowa>(`/pozycje-czynszowe/${id}`),
  create: (data: PozycjaCynszowaCreate) =>
    apiClient.post<PozycjaCzynszowa>('/pozycje-czynszowe', data),
  update: (id: number, data: PozycjaCynszowaUpdate) =>
    apiClient.put<PozycjaCzynszowa>(`/pozycje-czynszowe/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/pozycje-czynszowe/${id}`),
}

// Liczniki API
export const licznikiApi = {
  getAll: (params?: { lokal_id?: number; typ?: string; aktywny?: boolean }) =>
    apiClient.get<Licznik[]>('/liczniki', { params }),
  getById: (id: number) =>
    apiClient.get<Licznik>(`/liczniki/${id}`),
  create: (data: Partial<Licznik>) =>
    apiClient.post<Licznik>('/liczniki', data),
  update: (id: number, data: Partial<Licznik>) =>
    apiClient.put<Licznik>(`/liczniki/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/liczniki/${id}`),
  getHistoria: (id: number) =>
    apiClient.get<HistoriaLicznikaResponse>(`/liczniki/${id}/historia`),
  wymien: (id: number, data: any) =>
    apiClient.post(`/liczniki/${id}/wymien`, data),
}

// Odczyty Liczników API
export const odczytyApi = {
  create: (data: Partial<OdczytLicznika>) =>
    apiClient.post<OdczytLicznika>('/odczyty-licznikow', data),
}

// Import Excel API
export interface ImportLicznikiResult {
  success: boolean
  message?: string
  imported_count: number
  created_meters_count?: number
  errors: string[]
  warnings: string[]
  'wspólnota'?: string
  data_odczytu?: string
}

export const importApi = {
  // Direct URL — used for <a href> download link, bypasses axios
  licznikiTemplateUrl: '/api/import/template',

  licznikiUpload: async (
    file: File,
    wspolnotaId: number,
    dataOdczytu?: string
  ): Promise<ImportLicznikiResult> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('wspolnota_id', String(wspolnotaId))
    if (dataOdczytu) formData.append('data_odczytu', dataOdczytu)
    const response = await apiClient.post<ImportLicznikiResult>(
      '/import/liczniki',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },
}

// Emails API
export const emailsApi = {
  // Get email history with optional filters
  // 2026-04-20: URL zmieniony z /emails/history (404) na /email-management/email-history.
  // Backend zwraca EmailHistoryItem (MID-6), który jest kompatybilny strukturalnie z EmailHistory
  // w zakresie pól używanych w EmailHistoryPage (id, email_to, email_type, subject, status, sent_at, created_at).
  getHistory: (params?: {
    lokal_id?: number
    wspolnota_id?: number
    email_type?: string
    status?: string
    limit?: number
    offset?: number
  }) =>
    apiClient.get<EmailHistory[]>('/email-management/email-history', { params }),

  // Send monthly summary email to single lokal
  sendMonthly: (data: MonthlyEmailRequest) =>
    apiClient.post<EmailTaskResponse>('/emails/send-monthly-summary', data),

  // Send bulk monthly emails to all lokale in wspolnota (with consent)
  sendBulk: (data: BulkEmailRequest) =>
    apiClient.post<EmailTaskResponse>('/emails/send-bulk-monthly-summaries', data),

  // Send test email
  sendTest: (data: TestEmailRequest) =>
    apiClient.post<EmailTaskResponse>('/emails/send-test-email', data),
}

// Mieszkańcy API
export const mieszkancyApi = {
  create: (data: Partial<Mieszkaniec>) =>
    apiClient.post<Mieszkaniec>('/mieszkancy', data),
  update: (id: number, data: Partial<Mieszkaniec>) =>
    apiClient.put<Mieszkaniec>(`/mieszkancy/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/mieszkancy/${id}`),
}

// Opłaty Indywidualne API
export const oplatyIndywidualneApi = {
  getByLokalId: (lokal_id: number) =>
    apiClient.get<OplataIndywidualna[]>(`/oplaty-indywidualne/lokal/${lokal_id}/active`),
  getById: (id: number) =>
    apiClient.get<OplataIndywidualna>(`/oplaty-indywidualne/${id}`),
  create: (data: OplataIndywidualnaCreate) =>
    apiClient.post<OplataIndywidualna>('/oplaty-indywidualne', data),
  update: (id: number, data: OplataIndywidualnaUpdate) =>
    apiClient.put<OplataIndywidualna>(`/oplaty-indywidualne/${id}`, data),
  delete: (id: number) =>
    apiClient.delete(`/oplaty-indywidualne/${id}`),
}

// Administrator API
export const administratorApi = {
  get: () =>
    apiClient.get<Administrator>('/administrator'),
  update: (data: AdministratorUpdate) =>
    apiClient.put<Administrator>('/administrator', data),
}

// Rozliczenia API
export const rozliczeniaApi = {
  getAll: (params?: {
    wspolnota_id?: number
    rok?: number
    miesiac?: number
    status?: string
  }) => apiClient.get<RozliczenieOkresowe[]>('/rozliczenia', { params }),

  getById: (id: number) =>
    apiClient.get<RozliczenieDetailed>(`/rozliczenia/${id}`),

  create: (data: {
    wspolnota_id: number
    rok: number
    miesiac: number
  }) => apiClient.post<RozliczenieOkresowe>('/rozliczenia', data),

  zatwierdz: (id: number) =>
    apiClient.post(`/rozliczenia/${id}/zatwierdz`),
}

// Bank Import API
export const bankImportApi = {
  // Upload bank statement file
  upload: async (wspolnotaId: number, file: File): Promise<ImportBankowy> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`/api/bank-import/upload/${wspolnotaId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Błąd uploadu pliku' }))
      throw new Error(error.detail || 'Błąd uploadu pliku')
    }
    return response.json()
  },

  // Get all imports
  getAll: (params?: { wspolnota_id?: number }) =>
    apiClient.get<ImportBankowy[]>('/bank-import', { params }),

  // Get import with transactions
  getById: (importId: number) =>
    apiClient.get<ImportBankowyWithTransakcje>(`/bank-import/${importId}`),

  // Get transactions for import
  getTransakcje: (importId: number, statusFilter?: string) =>
    apiClient.get<TransakcjaBankowa[]>(`/bank-import/${importId}/transakcje`, {
      params: statusFilter ? { status_filter: statusFilter } : undefined
    }),

  // Match transaction manually
  matchTransaction: (transakcjaId: number, data: MatchTransactionRequest) =>
    apiClient.put<TransakcjaBankowa>(`/bank-import/transakcje/${transakcjaId}/match`, data),

  // Ignore transaction
  ignoreTransaction: (transakcjaId: number) =>
    apiClient.put(`/bank-import/transakcje/${transakcjaId}/ignore`),

  // Create payments from matched transactions
  createPayments: (data: CreatePaymentsRequest) =>
    apiClient.post<CreatePaymentsResponse>('/bank-import/create-payments', data),

  // Delete import
  delete: (importId: number) =>
    apiClient.delete(`/bank-import/${importId}`),
}

// Extended Reports API (FAZA 3.2)
export const reportsExtendedApi = {
  // Raport zadłużeń
  getZadluzenia: (wspolnotaId: number, tylkoPrzeterminowane?: boolean, minKwota?: number) =>
    apiClient.get<ZadluzeniaRaport>(`/reports-extended/zadluzenia/${wspolnotaId}`, {
      params: { tylko_przeterminowane: tylkoPrzeterminowane, min_kwota: minKwota }
    }),

  // Export zadłużeń do Excel
  exportZadluzeniaExcel: (wspolnotaId: number, tylkoPrzeterminowane?: boolean) =>
    `/api/reports-extended/zadluzenia-excel/${wspolnotaId}?tylko_przeterminowane=${tylkoPrzeterminowane || false}`,

  // Analiza zużycia mediów
  getZuzycieMediow: (wspolnotaId: number, rok?: number, typLicznika?: string) =>
    apiClient.get<ZuzycieMediowRaport>(`/reports-extended/zuzycie-mediow/${wspolnotaId}`, {
      params: { rok, typ_licznika: typLicznika }
    }),

  // Export zużycia mediów do Excel
  exportZuzycieMediowExcel: (wspolnotaId: number, rok?: number) =>
    `/api/reports-extended/zuzycie-mediow-excel/${wspolnotaId}?rok=${rok || new Date().getFullYear()}`,

  // Porównanie miesięczne
  getPorownanieMiesieczne: (wspolnotaId: number, rok1?: number, rok2?: number) =>
    apiClient.get<PorownanieRaport>(`/reports-extended/porownanie-miesieczne/${wspolnotaId}`, {
      params: { rok1, rok2 }
    }),

  // Export porównania do Excel
  exportPorownanieExcel: (wspolnotaId: number, rok1?: number, rok2?: number) =>
    `/api/reports-extended/porownanie-miesieczne-excel/${wspolnotaId}?rok1=${rok1 || new Date().getFullYear() - 1}&rok2=${rok2 || new Date().getFullYear()}`,

  // Dashboard stats dla wspólnoty
  getDashboardStats: (wspolnotaId: number) =>
    apiClient.get<WspolnotaDashboardStats>(`/reports-extended/dashboard-stats/${wspolnotaId}`),
}

// Export API — URL helpers dla eksportów PDF/Excel (HI-1 + HI-3)
export const exportApi = {
  oplatyLokaluPdf: (lokalId: number) => `/api/export/oplaty-lokalu-pdf/${lokalId}`,
  oplatyLokaluExcel: (lokalId: number) => `/api/export/oplaty-lokalu-excel/${lokalId}`,
  stawkiPdf: (wspolnotaId: number) => `/api/export/stawki-pdf/${wspolnotaId}`,
  stawkiExcel: (wspolnotaId: number) => `/api/export/stawki-excel/${wspolnotaId}`,
}

// Notifications API (FAZA 3.3)
export const notificationsApi = {
  // Statystyki powiadomień
  getStats: (wspolnotaId?: number) =>
    apiClient.get<NotificationStats>('/notifications/stats', {
      params: wspolnotaId ? { wspolnota_id: wspolnotaId } : undefined
    }),

  // Oczekujące powiadomienia - przeterminowane płatności
  getPendingOverdue: (wspolnotaId?: number, dniPoTerminie?: number) =>
    apiClient.get<PendingNotification[]>('/notifications/pending/overdue', {
      params: { wspolnota_id: wspolnotaId, dni_po_terminie: dniPoTerminie }
    }),

  // Oczekujące powiadomienia - legalizacja liczników
  getPendingLegalization: (dniOstrzezenia?: number) =>
    apiClient.get<PendingNotification[]>('/notifications/pending/legalization', {
      params: { dni_ostrzezenia: dniOstrzezenia }
    }),

  // Oczekujące powiadomienia - brakujące odczyty
  getPendingReadings: (wspolnotaId?: number) =>
    apiClient.get<PendingNotification[]>('/notifications/pending/readings', {
      params: wspolnotaId ? { wspolnota_id: wspolnotaId } : undefined
    }),

  // Ręczne wyzwolenie sprawdzania przeterminowanych płatności
  triggerOverdueCheck: (request: NotificationTriggerRequest) =>
    apiClient.post<NotificationTriggerResponse>('/notifications/trigger/overdue', request),

  // Ręczne wyzwolenie sprawdzania legalizacji
  triggerLegalizationCheck: (request: NotificationTriggerRequest) =>
    apiClient.post<NotificationTriggerResponse>('/notifications/trigger/legalization', request),

  // Ręczne wyzwolenie sprawdzania brakujących odczytów
  triggerReadingsCheck: (request: NotificationTriggerRequest) =>
    apiClient.post<NotificationTriggerResponse>('/notifications/trigger/readings', request),

  // Wysłanie pojedynczego przypomnienia o przeterminowanej płatności
  triggerSingleOverdue: (lokalId: number) =>
    apiClient.post<NotificationTriggerResponse>(`/notifications/trigger/single/overdue/${lokalId}`),

  // Wysłanie pojedynczego przypomnienia o odczycie
  triggerSingleReading: (lokalId: number) =>
    apiClient.post<NotificationTriggerResponse>(`/notifications/trigger/single/reading/${lokalId}`),

  // Historia powiadomień
  getHistory: (params?: {
    wspolnota_id?: number
    email_type?: string
    status?: string
    limit?: number
    offset?: number
  }) => apiClient.get<NotificationHistoryResponse>('/notifications/history', { params }),
}

// ============================================================================
// Owner Portal API (FAZA 3.4)
// ----------------------------------------------------------------------------
// Uwaga (2026-04-20): Backend refactoruje owner-portal na JWT — axios interceptor
// wysyła Authorization: Bearer <JWT> automatycznie. Wszystkie endpointy
// GET /owner-portal/* są wywoływane bez dodatkowych parametrów auth (backend
// wyciąga mieszkaniec_id z JWT). Zostawiamy legacy `token` param na wypadek
// gdyby backend jeszcze używał starej formy — backend ignoruje nieznane params.
// Dla nowego Resident Portal frontend wywołuje TE endpointy BEZ tokena w query.
// ============================================================================

export const ownerPortalApi = {
  // Legacy: token-based access (request-access) — nadal dostępny dla kompatybilności
  requestAccess: (request: OwnerTokenRequest) =>
    apiClient.post<OwnerTokenResponse>('/owner-portal/request-access', request),

  validateToken: (token: string) =>
    apiClient.get<{ valid: boolean; lokal_id: number; lokal_numer: string; wspolnota_nazwa: string }>(
      '/owner-portal/validate-token',
      { params: { token } }
    ),

  // ---- JWT-scoped endpoints (Resident Portal) ----
  // Bearer token automatycznie przez axios interceptor. Dla legacy token-flow
  // przekaż `token` jako param — backend akceptuje oba tryby podczas migracji.
  getDashboard: (token?: string) =>
    apiClient.get<OwnerDashboard>('/owner-portal/dashboard', {
      params: token ? { token } : undefined,
    }),

  getPayments: (token?: string, rok?: number, limit?: number, offset?: number) =>
    apiClient.get<OwnerPaymentHistory>('/owner-portal/payments', {
      params: { token, rok, limit, offset },
    }),

  getBalance: (token?: string) =>
    apiClient.get<OwnerBalanceInfo>('/owner-portal/balance', {
      params: token ? { token } : undefined,
    }),

  getMeters: (token?: string, rok?: number) =>
    apiClient.get<OwnerMeterInfo>('/owner-portal/meters', {
      params: { token, rok },
    }),

  getSettlements: (token?: string, rok?: number, limit?: number) =>
    apiClient.get<OwnerSettlementHistory>('/owner-portal/settlements', {
      params: { token, rok, limit },
    }),

  getEmails: (token?: string, limit?: number) =>
    apiClient.get<OwnerEmailHistoryResponse>('/owner-portal/emails', {
      params: { token, limit },
    }),
}

// Obiekty Dodatkowe API (Garaże, komórki, piwnice)
export const obiektyDodatkoweApi = {
  // Get all objects (optionally filtered)
  getAll: (params?: {
    lokal_id?: number
    typ?: string
    aktywny?: boolean
  }) => apiClient.get<ObiektDodatkowy[]>('/obiekty-dodatkowe', { params }),

  // Get objects by lokal
  getByLokalId: (lokalId: number) =>
    apiClient.get<ObiektDodatkowy[]>('/obiekty-dodatkowe', {
      params: { lokal_id: lokalId }
    }),

  // Get single object
  getById: (id: number) =>
    apiClient.get<ObiektDodatkowy>(`/obiekty-dodatkowe/${id}`),

  // Create new object
  create: (data: ObiektDodatkowyCreate) =>
    apiClient.post<ObiektDodatkowy>('/obiekty-dodatkowe', data),

  // Update object
  update: (id: number, data: ObiektDodatkowyUpdate) =>
    apiClient.put<ObiektDodatkowy>(`/obiekty-dodatkowe/${id}`, data),

  // Delete object
  delete: (id: number) =>
    apiClient.delete(`/obiekty-dodatkowe/${id}`),
}

// ============================================================================
// Admin API (MID-5) — user management + mieszkancy assignment
// ============================================================================

export interface AdminUserItem {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  is_active: boolean
  is_superuser: boolean
  created_at: string
  mieszkaniec_id: number | null
  mieszkaniec_name: string | null
  lokal_numer: string | null
}

export interface AdminMieszkaniecItem {
  id: number
  imie: string | null
  nazwisko: string | null
  email: string | null
  telefon: string | null
  typ: string
  glowny: boolean
  aktywny: boolean
  lokal_id: number
  lokal_numer: string
  user_id: number | null
  user_email: string | null
}

// Resident access lifecycle (invite / revoke / status) — konsumowany przez
// admin panel (MieszkancyAssignmentTab). Backend dostarcza endpointy równolegle.
export interface ResidentInvitePending {
  token_hash?: string
  expires_at: string | null
  sent_at: string | null
}

export interface ResidentAccessStatus {
  mieszkaniec_id: number
  has_account: boolean
  user_id: number | null
  is_active: boolean
  last_login: string | null
  last_login_ip: string | null
  // `pending_invite` jest ujęte jako obiekt z datami (backend zwraca null gdy brak).
  // Trzymamy kompatybilność z komponentem admin: boolean check (truthy) nadal działa.
  pending_invite: ResidentInvitePending | null
  invite_expires_at: string | null
  invite_sent_at: string | null
}

export interface ResidentInviteResponse {
  success: boolean
  mieszkaniec_id: number
  email: string
  expires_at: string | null
  message?: string
}

export const adminApi = {
  getUsers: (params?: { skip?: number; limit?: number }) =>
    apiClient.get<AdminUserItem[]>('/admin/users', { params }),
  activateUser: (userId: number, isActive: boolean) =>
    apiClient.patch<{ success: boolean; user_id: number; is_active: boolean }>(
      `/admin/users/${userId}/activate`,
      { is_active: isActive }
    ),
  getMieszkancy: (params?: { lokal_id?: number; has_user?: boolean; skip?: number; limit?: number }) =>
    apiClient.get<AdminMieszkaniecItem[]>('/admin/mieszkancy', { params }),
  assignUser: (mieszkaniecId: number, userId: number) =>
    apiClient.patch<{ success: boolean; mieszkaniec_id: number; user_id: number; user_email: string }>(
      `/admin/mieszkancy/${mieszkaniecId}/assign-user`,
      { user_id: userId }
    ),
  unassignUser: (mieszkaniecId: number) =>
    apiClient.delete<{ success: boolean; mieszkaniec_id: number; message: string }>(
      `/admin/mieszkancy/${mieszkaniecId}/unassign-user`
    ),

  // --- Resident access lifecycle (invite / status / revoke) ---
  // Backend kontrakty dostarcza agent C równolegle. Utrzymujemy 1:1 nazwy
  // wywoływane z MieszkancyAssignmentTab, aby build się kompilował.
  getAccessStatus: (mieszkaniecId: number) =>
    apiClient.get<ResidentAccessStatus>(
      `/admin/mieszkancy/${mieszkaniecId}/access-status`
    ),
  sendResidentInvite: (mieszkaniecId: number) =>
    apiClient.post<ResidentInviteResponse>(
      `/admin/mieszkancy/${mieszkaniecId}/send-invite`
    ),
  revokeResidentAccess: (mieszkaniecId: number) =>
    apiClient.post<{ success: boolean; mieszkaniec_id: number; message?: string }>(
      `/admin/mieszkancy/${mieszkaniecId}/revoke-access`
    ),
}

// ============================================================================
// Email Management API (MID-6) — RODO consent + history + stats
// ============================================================================

export interface MieszkaniecEmailStatus {
  id: number
  imie: string | null
  nazwisko: string | null
  email: string | null
  email_zgoda: boolean
  lokal_id: number
  lokal_numer: string
  wspolnota_nazwa: string | null
  aktywny: boolean
}

export interface EmailHistoryItem {
  id: number
  email_to: string
  email_type: string
  subject: string | null
  status: string
  error_message: string | null
  sent_at: string | null
  created_at: string
  lokal_numer: string | null
  mieszkaniec_nazwa: string | null
}

export interface EmailStats {
  total_sent: number
  total_failed: number
  total_pending: number
  success_rate: number
  last_24h: number
  last_7days: number
  by_type: Record<string, number>
}

export interface EmailContent {
  id: number
  email_to: string
  subject: string | null
  email_type: string
  email_body: string | null
  sent_at: string | null
  created_at: string
}

export const emailManagementApi = {
  getEmailStatus: (params?: {
    wspolnota_id?: number
    email_zgoda?: boolean
    has_email?: boolean
    search?: string
    lokal_numer?: string
  }) => apiClient.get<MieszkaniecEmailStatus[]>('/email-management/mieszkancy-email-status', { params }),

  bulkUpdateConsent: (payload: { mieszkaniec_ids: number[]; email_zgoda: boolean }) =>
    apiClient.post<{ message: string; updated_count: number; email_zgoda: boolean }>(
      '/email-management/bulk-update-consent',
      payload
    ),

  getHistory: (params?: {
    lokal_id?: number
    email_type?: string
    status?: string
    limit?: number
    offset?: number
  }) => apiClient.get<EmailHistoryItem[]>('/email-management/email-history', { params }),

  getStats: (params?: { wspolnota_id?: number }) =>
    apiClient.get<EmailStats>('/email-management/email-stats', { params }),

  getEmailContent: (emailId: number) =>
    apiClient.get<EmailContent>(`/email-management/email-content/${emailId}`),
}

// ============================================================================
// Zadłużenia API (N-6) — CRUD + saldo lokalu
// Backend: routes_debt_payments.py
// ============================================================================

export const zadluzeniaApi = {
  getAll: (params?: {
    skip?: number
    limit?: number
    lokal_id?: number
    status?: string
  }) => apiClient.get<ZadluzenieApi[]>('/zadluzenia', { params }),

  getById: (id: number) =>
    apiClient.get<ZadluzenieApi>(`/zadluzenia/${id}`),

  create: (data: ZadluzenieCreateApi) =>
    apiClient.post<ZadluzenieApi>('/zadluzenia', data),

  update: (id: number, data: ZadluzenieUpdateApi) =>
    apiClient.put<ZadluzenieApi>(`/zadluzenia/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/zadluzenia/${id}`),

  getLokalSaldo: (lokalId: number) =>
    apiClient.get<SaldoLokaluApi>(`/lokale/${lokalId}/saldo`),
}

// ============================================================================
// Płatności API (N-6) — CRUD + zarejestruj (auto-rozlicza zadłużenia)
// Backend: routes_debt_payments.py
// ============================================================================

export const platnosciApi = {
  getAll: (params?: {
    skip?: number
    limit?: number
    lokal_id?: number
    rozliczenie_id?: number
  }) => apiClient.get<PlatnoscApi[]>('/platnosci', { params }),

  getById: (id: number) =>
    apiClient.get<PlatnoscApi>(`/platnosci/${id}`),

  create: (data: PlatnoscCreateApi) =>
    apiClient.post<PlatnoscApi>('/platnosci', data),

  update: (id: number, data: PlatnoscUpdateApi) =>
    apiClient.put<PlatnoscApi>(`/platnosci/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/platnosci/${id}`),

  // POST /platnosci/zarejestruj — tworzy płatność i auto-rozlicza aktywne zadłużenia
  zarejestruj: (data: PlatnoscCreateApi) =>
    apiClient.post<PlatnoscApi>('/platnosci/zarejestruj', data),
}
